<template>
  <div style="width: 100%; height: 100%">
    <div
      v-if="uuid"
      :id="`video-container-${uuid}`"
      class="video-container"
      style="width: 100%; height: 100%; border-radius: 0.8rem"
    ></div>

    <!-- Face Detection Sidebar -->
    <face-detection-sidenav
      :is-sidebar-active.sync="isFaceDetectionSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="handleRefetchData"
      :watchlist-options="computedWatchlistOptions"
      :snapshot-data="snapshotImage"
    />
  </div>
</template>

<script>
import CamerasSdk from "@omnicloud/cameras-sdk-v2";
import { SLOT_TYPES, SOURCES } from "@/config/layoutConfig";
import layoutMixin from "@/mixins/layoutMixin";
import { v4 as uuidv4 } from "uuid";
import store from "@/store";
import { EventBus } from "@/libs/event-bus";
import FaceDetectionSidenav from "@/views/Ntechlab/Face/Sidenav.vue";
import axios from "@/libs/axios";

export default {
  mixins: [layoutMixin],
  components: {
    FaceDetectionSidenav,
  },
  props: {
    pos: {
      type: Number | String,
    },
    parentId: {
      type: Number | String,
    },
    cameraId: {
      type: Number | String,
      default: null,
    },
    cameraProxyId: {
      type: Number | String,
      default: null,
    },
    cameraType: {
      type: String,
      default: null,
    },
    dateRange: {
      type: Object,
    },
    nameSufix: {
      type: String,
      default: "",
    },
    host: {
      type: String,
      default: window.__env.VUE_APP_BASE_URL,
    },
    tabInfo: Object,
    config: Object,
  },
  data() {
    return {
      uuid: null,
      camerasSdk: null,
      snapshotImage: null,
      isFaceDetectionSidebarActive: false,
      isAdd: true,
      itemEdit: {},
      watchlistOptions: [],
      initializationGeneration: 0,
      widgetDestroyed: false,
      listenersAttached: false,
      snapshotTakenHandler: null,
      cloneInstanceHandler: null,
      exportVideoHandler: null,
      sdkReadyHandler: null,
    };
  },
  created() {
    this.snapshotTakenHandler = (event) => {
      this.onSnapshotTakenDynamicEnrollment(event.detail);
    };
    this.cloneInstanceHandler = (data) => this.cloneInstance(data);
    this.exportVideoHandler = (data) => this.onExportVideo(data);
    this.sdkReadyHandler = () => {
      if (!this.widgetDestroyed) this.setListeners();
    };
  },
  computed: {
    computedWatchlistOptions() {
      const storeOptions = this.$store.getters["ntechlab_watch_list/getSelectedItems"];
      const localOptions = this.watchlistOptions;

      return storeOptions && storeOptions.length > 0 ? storeOptions : localOptions;
    },
  },
  async mounted() {
    const initializationGeneration = ++this.initializationGeneration;

    EventBus.on(`setLang`, this.onSetLang);
    this.uuid = uuidv4();

    let token = localStorage.getItem("accessToken");

    if (this.host !== window.__env.VUE_APP_BASE_URL) {
      const externalUser = this.$store.state.user.user.external_users.find(
        (external_user) => external_user.host === this.host
      );

      if (externalUser) token = externalUser.token;
    }

    const config = {
      id: this.uuid,
      cameraId: this.cameraProxyId,
      container: `#video-container-${this.uuid}`,
      token,
      apiUrl: `${this.host}/v1`,
      startDate: null,
      endDate: null,
      pusher_app_key: window.__env?.VUE_APP_LARAVEL_PUSHER_APP_KEY,
      pusher_host: window.__env?.VUE_APP_LARAVEL_PUSHER_HOST,
      pusher_port: window.__env?.VUE_APP_LARAVEL_PUSHER_PORT,
      pusher_app_cluster: window.__env.VUE_APP_LARAVEL_PUSHER_APP_CLUSTER || "mt1",
      mode: "live_video",
      layout: this.config ? this.config.layout : "default",
      synced: this.config ? this.config.synced : true,
      tabId: this.tabInfo?.id,
      lang: this.$i18n.locale,
      logsUrl: window.__env.VUE_APP_LOGS_MS_URL, //(hardcode logs)
    };

    if (this.cameraType === "hanwha_camera") {
      const { liveSessionId, recordingSessionId } = await this.getSessionId();
      if (!this.isInitializationCurrent(initializationGeneration)) return;

      config.liveSessionId = liveSessionId;
      config.recordingSessionId = recordingSessionId;
    }

    if (this.dateRange.start && this.dateRange.end) {
      config.startDate = this.dateRange.start;
      config.endDate = this.dateRange.end;
      config.mode = "recorded_video";
    }

    await this.$nextTick();
    if (!this.isInitializationCurrent(initializationGeneration)) return;

    this.camerasSdk = new CamerasSdk(config);
    this.camerasSdk.on("ready", this.sdkReadyHandler);

    window.addEventListener(
      "snapshot-taken-dynamic-enrollment",
      this.snapshotTakenHandler
    );

    await this.loadWatchlistOptions();
    if (!this.isInitializationCurrent(initializationGeneration)) return;

    if (!window.cameraWidgetInstances) {
      window.cameraWidgetInstances = new Map();
    }
    window.cameraWidgetInstances.set(this.uuid, this);
  },
  beforeDestroy() {
    this.widgetDestroyed = true;
    this.initializationGeneration += 1;

    EventBus.off(`setLang`, this.onSetLang);
    this.removeListeners();

    if (this.camerasSdk) {
      try {
        this.camerasSdk.destroy();
      } catch (error) {
        console.error(error);
      } finally {
        this.camerasSdk = null;
      }
    }

    window.removeEventListener(
      "snapshot-taken-dynamic-enrollment",
      this.snapshotTakenHandler
    );

    if (window.cameraWidgetInstances) {
      window.cameraWidgetInstances.delete(this.uuid);
    }
  },
  methods: {
    isInitializationCurrent(generation) {
      return !this.widgetDestroyed && this.initializationGeneration === generation;
    },
    onSetLang(lang) {
      this.camerasSdk?.setLang(lang);
    },
    setListeners() {
      if (!this.camerasSdk || this.listenersAttached) return;

      this.listenersAttached = true;
      this.camerasSdk.on("clone:instance", this.cloneInstanceHandler);
      this.camerasSdk.on("export:video", this.exportVideoHandler);
    },
    removeListeners() {
      try {
        if (this.camerasSdk && typeof this.camerasSdk.off === "function") {
          this.camerasSdk.off("ready", this.sdkReadyHandler);
          this.camerasSdk.off("clone:instance", this.cloneInstanceHandler);
          this.camerasSdk.off("export:video", this.exportVideoHandler);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.listenersAttached = false;
      }
    },
    cloneInstance(data) {
      this.setSlotData(
        SLOT_TYPES.CAM_LIVE,
        data.camera.name || `CAM_${data.camera.id}`,
        {
          camera: {
            ...data.camera,
            camera_type: data.camera.camera_type,
            camera_id: data.camera.id,
          },
        },
        SOURCES.ALARM
      );
    },
    onSnapshotTakenDynamicEnrollment(data) {
      if (typeof data === "string") {
        this.snapshotImage = {
          data: data,
          timestamp: new Date().toISOString(),
          type: "dynamic-enrollment-snapshot",
          success: true,
        };

        this.openFaceDetectionSidebar();
      } else if (data && data.type === "dynamic-enrollment-error") {
        this.snapshotImage = {
          error: data.error,
          timestamp: new Date().toISOString(),
          type: "dynamic-enrollment-error",
          success: false,
        };
      } else {
        this.snapshotImage = null;
      }
    },
    async openFaceDetectionSidebar() {
      // Always ensure watchlist options are loaded and up to date
      const storeOptions = this.$store.getters["ntechlab_watch_list/getSelectedItems"];
      if (!storeOptions || storeOptions.length === 0) {
        await this.loadWatchlistOptions();
      }

      this.isAdd = true;
      this.itemEdit = {};
      this.isFaceDetectionSidebarActive = true;
    },
    async loadWatchlistOptions() {
      try {
        const response = await store.dispatch("ntechlab_watch_list/getAll");

        // Wait for the store to update and then get the items
        await this.$nextTick();
        const watchlistItems = store.getters["ntechlab_watch_list/getSelectedItems"];

        this.watchlistOptions = watchlistItems;
      } catch (error) {
        this.watchlistOptions = [];
      }
    },
    handleRefetchData() {},
    getLatestSnapshot() {
      return this.snapshotImage;
    },
    onExportVideo(data) {
      const exportChannel = data.id;
      const fileName = data.fileName || data.name;
      const titleDownload = fileName;
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const downloadAt =
        `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ` +
        `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
      const notificationId = `cameras-sdk2-export-${exportChannel}`;
      const notification = {
        id: notificationId,
        progress: 0,
        completed: false,
        view: "",
        url: "",
        title: titleDownload,
        fileName,
        download: fileName,
        body: "Descargando...",
        error: false,
        timestamp: downloadAt,
      };

      store.dispatch("grid/addDownloadNotification", notification);

      window.Echo.private(exportChannel)
        .listen(".update", (data) => {
          if (data.message.status === "ok") {
            const notification = {
              id: notificationId,
              progress: 100,
              completed: true,
              view: "",
              url: data.message.url,
              title: titleDownload,
              fileName,
              download: fileName,
              body: "Descarga completa",
              error: false,
              timestamp: downloadAt,
            };
            store.dispatch("grid/updateDownloadNotification", notification);
            // window.Echo.leave(exportChannel);
            return;
          } else {
            const notification = {
              id: notificationId,
              progress: 100,
              completed: true,
              view: "",
              url: "",
              title: titleDownload,
              body: "Ocurrio un error",
              error: true,
              timestamp: downloadAt,
            };
            store.dispatch("grid/updateDownloadNotification", notification);
            // window.Echo.leave(exportChannel);
          }
        })
        .listen(".status", (data) => {
          const percent = data.message.percent || 10;
          const notification = {
            id: notificationId,
            progress: percent,
            completed: false,
            view: "",
            url: "",
            title: titleDownload,
            body: this.getProgressMessage(percent),
            error: false,
            timestamp: downloadAt,
          };
          store.dispatch("grid/updateDownloadNotification", notification);
        });
    },
    async fetchSessionId() {
      try {
        const { data } = await axios.get(
          `${window.__env.VUE_APP_BASE_URL}/v1/stream/${this.cameraProxyId}/session-id`
        );

        const sessionId = data?.data?.sessionId;

        return sessionId;
      } catch (error) {
        return null;
      }
    },
    async getSessionId() {
      let liveSessionId = sessionStorage.getItem("liveSessionId");
      let recordingSessionId = sessionStorage.getItem("recordingSessionId");
      if (
        liveSessionId === null ||
        liveSessionId === "undefined" ||
        liveSessionId === "null"
      ) {
        liveSessionId = await this.fetchSessionId();
        sessionStorage.setItem("liveSessionId", liveSessionId);
      }

      if (
        recordingSessionId === null ||
        recordingSessionId === "undefined" ||
        recordingSessionId === "null"
      ) {
        recordingSessionId = await this.fetchSessionId();
        sessionStorage.setItem("recordingSessionId", recordingSessionId);
      }

      return {
        liveSessionId,
        recordingSessionId,
      };
    },
    getProgressMessage(p = 0) {
      if (p < 50) return "Generando video...";
      if (p < 80) return "Aplicando marca de agua y timestamp...";
      if (p < 90) return "Subiendo archivo seguro...";
      if (p < 100) return "Generando enlace temporal...";
      return "Listo para descargar";
    },
  },
};
</script>

<style lang="scss">
.video-container {
  & > div {
    width: 100%;
    height: 100%;
  }
}
</style>
